<template>
	<div class="content">
		<section
			id="hero"
			class="container-expanded container band"
		>
			<h1>The Restorative Classroom Playbook</h1>
			<h2>Led by Adam Voigt</h2>
			<h4>Friday 4th December 2026 | 10am - 3pm AEDT</h4>
		</section>

		<section
			id="intro"
			class="container-expanded container cols band"
		>
			<div class="col col-2" />
			<div class="col col-center text-center">
				<h3>Move from Compliance to Cooperation<br />Without Carrying It All Alone</h3>
				<p><strong>A live, online, interactive workshop for classroom and specialist teachers across all systems and year levels.</strong></p>
				<p>This isn't another behaviour management program.</p>
				<p>It's a practical culture reset that hands you the language, response pathways and mindset shifts to build a calm, cooperative classroom – where responsibility is shared and teaching feels sustainable again.</p>
				<p></p>
				<p>
					<strong>Workshop: 4 December 2026<br />10am – 3pm AEDT</strong>
				</p>
				<p>
					<a
						href="#register"
						class="btn btn-primary"
						>Register Now</a
					>
				</p>
			</div>
			<div class="col col-2" />
		</section>

		<section
			id="workshop-benefits"
			class="cols container-expanded container-no-pad"
		>
			<div class="col col-6">
				<div class="col-inner">
					<h2>Is teaching feeling harder than it should?</h2>
					<p><strong>Right now, you're probably:</strong></p>
					<ul>
						<li>Redirecting more than teaching</li>
						<li>Tracking constant low-level student behaviours</li>
						<li>Managing emotional tension in the room</li>
						<li>Losing learning time</li>
						<li>Leaving school each day feeling utterly drained</li>
					</ul>
					<p>From the outside, things may look fine.</p>
					<p>But you know how much energy it takes.</p>
					<p>The Restorative Classroom Playbook will help you stop carrying the behavioural load alone – and start building a classroom culture where responsibility is shared and trust is rebuilt.</p>
					<p>We're not doing it through slogans, murals or empty promises.</p>
					<p>You're about to learn the language, the structure and practical shifts you can rely on.</p>
				</div>
			</div>
			<div
				id="change-section"
				class="col col-6"
			>
				<div class="col-inner">
					<h2>This workshop will change all that</h2>
					<p><strong>When a student pushes back…</strong> You'll know exactly what to say.</p>
					<p><strong>When low-level behaviour starts spreading…</strong> You'll have a clear next step.</p>
					<p><strong>When emotions run high…</strong> You'll have stored responses you can rely on.</p>
					<p><strong>When a lesson gets derailed…</strong> You'll know how to reset it without losing authority.</p>
					<p><strong>And when the day ends…</strong> You won't feel like you carried it all alone.</p>
					<p>You'll leave with your own documented Restorative Classroom Playbook – built for your class, not someone else's.</p>
				</div>
			</div>
		</section>

		<section
			id="pl-day-bonuses"
			class="cols container-expanded container-no-pad"
		>
			<div
				id="pl-day-section"
				class="col col-6"
			>
				<div class="col-inner">
					<h2>This isn't just another PL Day</h2>
					<p><strong>Wondering what makes this workshop different to all the others?</strong></p>
					<ul>
						<li>Culture-first, not incident-first</li>
						<li>Built on RP2.0: Language, Conduct, Mindset</li>
						<li>Road-tested with thousands of teachers</li>
						<li>Practical and immediately implementable (modelling, response pathways, live practice)</li>
						<li>Firm, fair and human – not permissive</li>
						<li>Designed for instant transfer in your next lesson</li>
					</ul>
					<p><strong>And importantly:</strong></p>
					<p>This isn't squeezed into a staff meeting.</p>
					<p>It's protected time away from the classroom – perhaps even away from the school gates – for you to step back, reset and see your classroom clearly.</p>
					<p>When you're inside the daily pressure, it's hard to see the currents you're swimming against.</p>
					<p>This workshop gives you space to think, practise and plan with intention.</p>
					<p><strong>It's not just another PL Day.</strong></p>
					<p><strong>It's perspective – and a working plan you can trust.</strong></p>
				</div>
			</div>
			<div class="col col-6">
				<div class="col-inner">
					<h2>Bonus resources (included in your ticket)</h2>
					<p>Plus, you get these extras to make your next lesson easier:</p>

					<p>
						<strong>Bonus 1: The Feelings Wheel Poster</strong><br />
						Kids can't use words they don't have. The Feelings Wheel gives them better options than "fine" and "angry," which makes your restorative chats shorter, calmer and more useful.
					</p>

					<p>
						<strong>Bonus 2: The Consistent Conversation Compass</strong><br />
						A one-pager that stops hard conversations going off the rails. Past, present, future, follow-up – you get a simple sequence you can lean on instead of winging it.
					</p>

					<p>
						<strong>Bonus 3: P3P3F3 Group Conflict Fast-Track</strong><br />
						A template for sorting group blow-ups quickly and properly, without an hour-long episode of Education CSI.
					</p>

					<p>
						<strong>Bonus 4: Restoring Teaching Study Guide</strong><br />
						A straight-talking guide to help you or your team go deeper into Restoring Teaching after the workshop. Key ideas, reflection prompts, and "OK, so what do we actually do now?" actions.
					</p>

					<p>
						<strong>Bonus 5: Restorative Operating Domains</strong><br />
						The classic 'To/With/For/Not' Model. A handy reminder to stay in the WITH space – firm, fair and human – when things get heated.
					</p>

					<p>
						<strong>Bonus 6: A Hands-On Guide to Running Circles</strong><br />
						A practical how-to for check-in, check-out and response circles. Timings, prompts and tips so you can run circles that work, not just talk-fests.
					</p>

					<p>
						<strong>Bonus 7: AI Classroom Support Pack</strong><br />
						Two AI tools built for real classrooms, not Silicon Valley:
					</p>
					<ul>
						<li><strong>Student Behaviour Genius:</strong> Helps you make sense of tricky behaviour and sketch out restorative responses and next steps.</li>
						<li><strong>Restorative Lesson Planner:</strong> Helps you bake restorative language and routines into your lesson plans, so this isn't "one more thing" on the side.</li>
					</ul>
				</div>
			</div>
		</section>

		<section class="alternatings cols container-expanded container-no-pad">
			<div class="col col-6">
				<div class="col-inner">
					<h3>About Adam</h3>
					<p><strong>Adam Voigt, Founder & CEO of Real Schools</strong></p>
					<p>Adam is a former school principal who's spent over 20 years leading real change in real schools. Through his work at Real Schools, he's helped thousands of educators shift culture, lift engagement, and build schools where both students and staff thrive.</p>
					<p>He's not a theorist – he's been in the trenches. He started this podcast to share the real, raw, and reflective stories behind school leadership and culture.</p>
					<p>He will go beyond strategy and theory to unpack the moments, mindset shifts, and decisions that truly shape our schools, and inspire you to create the changes in our educational landscape that our kids – and our educators – deserve.</p>
				</div>
			</div>
			<div
				id="adam"
				class="col col-6"
			></div>
		</section>

		<section
			id="what-well-cover"
			class="container-expanded container band"
		>
			<div class="cols">
				<div class="col text-center">
					<h2><strong>It’s time for a reset</strong></h2>
				</div>
			</div>
		</section>

		<section
			id="perfect-details"
			class="cols container-expanded container-no-pad"
		>
			<div
				id="perfect-section"
				class="col col-6"
			>
				<div class="col-inner">
					<h2>This workshop is perfect for you if:</h2>
					<ul>
						<li>Behaviour is chewing up more learning time than you'd like</li>
						<li>You're tired of feeling hyper-vigilant</li>
						<li>You want clarity instead of improvising in hard moments</li>
						<li>You're ready for something more than another behaviour strategy</li>
					</ul>
					<p>You don't have to be in crisis.</p>
					<p>You just have to know there's a better way to do this.</p>
				</div>
			</div>
			<div class="col col-6">
				<div class="col-inner">
					<h2>Workshop Details</h2>
					<p><strong>Date:</strong> Friday 4th December 2026</p>
					<p><strong>Time:</strong> 10am - 3pm AEDT</p>
					<p><strong>Format:</strong> Live, online and interactive.</p>
					<p>Make time away from your classroom – even away from the school gates – so you can think clearly, reset with intention and truly make the most of the day.</p>
				</div>
			</div>
		</section>

		<section
			id="register"
			class="container-expanded container band"
		>
			<div class="cols">
				<div class="col col-1" />
				<div class="col text-center">
					<h2>You don't have to carry it all alone</h2>
				</div>
				<div class="col col-1" />
			</div>
		</section>

		<section class="container-expanded container band">
			<div class="cols">
				<div class="col col-1" />
				<div class="col col-10">
					<HubspotFormComponent
						form-id="a4c1d5d9-e505-4b81-8c4e-210ba5a84ed4"
						intro-text="Click below to register for the workshop and reset your classroom culture."
						toggle-key="hasSubmittedTheRestorativeClassroomPlaybookDec2026Form"
					>
						<template #registered>
							<h3>You're registered!</h3>
							<p>We can't wait to see you in the workshop.</p>
						</template>
					</HubspotFormComponent>
				</div>
				<div class="col col-1" />
			</div>
		</section>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import heroImage from '@/images/the-restorative-classroom-playbook.webp';
import adamImage from '@/images/adam.jpg';
import { wrapBackgroundImageUrlToCss } from '@/helpers';
import HubspotFormComponent from '@/components/shared/HubspotFormComponent.vue';
export default defineComponent({
	components: {
		HubspotFormComponent,
	},
	computed: {
		heroimageurl() {
			return wrapBackgroundImageUrlToCss(heroImage);
		},
		adamimageurl() {
			return wrapBackgroundImageUrlToCss(adamImage);
		},
	},
});
</script>

<style scoped lang="scss">
.content {
	h1,
	h2,
	h3,
	h4 {
		// font-family: var(--rs-typography-font-body);
	}
}

section {
	--rs-local-container-max-width: 1600px;
}

#hero {
	--rs-local-container-max-width: 1200px;

	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	margin: 0;
	/* stylelint-disable-next-line declaration-property-value-no-unknown */
	background-image: v-bind(heroimageurl);
	background-position: center top 24%;
	background-size: cover;
	background-repeat: no-repeat;
	min-height: 400px;
	text-align: right;

	h1 {
		margin: 0 0 5px 0;
		color: white;
		font-size: 56px;
		text-shadow: 3px 3px 10px rgb(0 0 0 / 90%);
	}

	h2 {
		margin: 0;
		color: white;
		text-shadow: 3px 3px 10px rgb(0 0 0 / 90%);
	}

	h4 {
		margin: 0;
		color: white;
		text-shadow: 3px 3px 10px rgb(0 0 0 / 90%);
	}
}

#intro {
	background-color: rgb(var(--rs-color-brand-navy));
	color: white;

	h3 {
		margin-top: 0;
		color: rgb(var(--rs-color-brand-mustard));
	}
}

#to-know {
	background-color: rgb(var(--rs-color-brand-mustard));

	h3 {
		margin-top: 0;
		color: rgb(var(--rs-color-brand-navy));
	}

	p {
		margin-bottom: 10px;
	}
}

#details {
	background-color: #eaeef1;

	h2 {
		margin-top: 0;
		margin-bottom: 20px;
	}
}

.heading {
	background-color: rgb(var(--rs-color-brand-mustard));

	h3 {
		margin: 0;
		color: rgb(var(--rs-color-brand-navy));
	}
}

#workshop-benefits {
	h2 {
		margin-top: 0;
	}
}

#change-section {
	background-color: #eaeef1;
}

#pl-day-bonuses {
	h2 {
		margin-top: 0;
	}
}

#pl-day-section {
	background-color: #eaeef1;
}

.alternatings {
	h3 {
		margin-top: 0;
		margin-bottom: 0;
	}

	#adam {
		/* stylelint-disable-next-line declaration-property-value-no-unknown */
		background-image: v-bind(adamimageurl);
		background-position: center top -100px;
		background-size: cover;
		background-repeat: no-repeat;
		min-height: 400px;
	}
}

#what-well-cover {
	background-color: rgb(var(--rs-color-brand-mustard));
	padding: 30px 0;

	h2 {
		color: rgb(var(--rs-color-brand-navy));
	}
}

#perfect-details {
	h2 {
		margin-top: 0;
	}
}

#perfect-section {
	background-color: #eaeef1;
}

#register {
	background-color: rgb(var(--rs-color-brand-navy));
	color: white;

	h2 {
		color: rgb(var(--rs-color-brand-red));
	}

	h3 {
		color: rgb(var(--rs-color-brand-red));
	}
}
</style>
